import { createContext, useCallback, useEffect, useState } from "react";
import { Language, onChangeLanguage } from "../../i18n/config"; //
import {
  applyDirectionColors,
  countryToScheme,
  DEFAULT_DIRECTION_SCHEME,
  DirectionColors,
  DirectionSchemeName,
} from "../../utils/directionColors";
import { clampSlippageBps } from "../../utils/amount/slippage";
import { defaultSettings } from "./helpers";
import {
  ISettingsContextProviderProps,
  ISettingsContextType,
  OptionsChainSettings,
  SettingsEnum,
  SFXSettings,
} from "./types";

// Re-export every public symbol so existing importers resolve unchanged.
export type {
  SFXSettings,
  OptionsChainSettings,
  LanguageSettings,
} from "./types";
export { SettingsEnum } from "./types";
export { settingsEnumTitle } from "./helpers";

export const SettingsContext =
  createContext<ISettingsContextType>(defaultSettings);

export function SettingsContextProvider({
  children,
}: ISettingsContextProviderProps) {
  const [sfx, setSfx] = useState<Set<SFXSettings>>(defaultSettings.sfx);
  const [optionsChain, setOptionsChain] = useState<Set<OptionsChainSettings>>(
    defaultSettings.optionsChain
  );
  const [language, setLanguage] = useState<Language>("en");
  const [directionScheme, setDirectionScheme] = useState<DirectionSchemeName>(
    DEFAULT_DIRECTION_SCHEME
  );
  const [customDirection, setCustomDirectionState] = useState<DirectionColors>(
    defaultSettings.customDirection
  );
  const [slippageBps, setSlippageBpsState] = useState<number>(
    defaultSettings.slippageBps
  );

  const updateLocalStorage = useCallback(
    (setting: SettingsEnum) => {
      if (setting === SettingsEnum.OPTIONS_CHAIN) {
        localStorage.setItem(
          SettingsEnum.OPTIONS_CHAIN,
          JSON.stringify(Array.from(optionsChain))
        );
      }

      if (setting === SettingsEnum.SFX) {
        localStorage.setItem(SettingsEnum.SFX, JSON.stringify(Array.from(sfx)));
      }

      if (setting === SettingsEnum.LANGUAGE) {
        localStorage.setItem(SettingsEnum.LANGUAGE, language);
        onChangeLanguage(language);
      }

      if (setting === SettingsEnum.DIRECTION_COLORS) {
        localStorage.setItem(SettingsEnum.DIRECTION_COLORS, directionScheme);
      }

      if (setting === SettingsEnum.CUSTOM_DIRECTION) {
        localStorage.setItem(
          SettingsEnum.CUSTOM_DIRECTION,
          JSON.stringify(customDirection)
        );
      }
    },
    [optionsChain, sfx, language, directionScheme, customDirection]
  );

  // Initialization hook
  useEffect(() => {
    const optSettings = localStorage.getItem(SettingsEnum.OPTIONS_CHAIN);
    const sfxSettings = localStorage.getItem(SettingsEnum.SFX);
    const languageSetting = localStorage.getItem(SettingsEnum.LANGUAGE);

    if (optSettings) {
      const parsedOptSettings = new Set(
        JSON.parse(optSettings) as OptionsChainSettings[]
      );
      setOptionsChain(parsedOptSettings);
    } else {
      // Check if settings were all intentionally set as disabled
      if (optionsChain.has("disable_all")) {
        setOptionsChain(new Set<OptionsChainSettings>([]));
      } else {
        // If the key doesn't exist, re-populate settings
        setOptionsChain(
          new Set<OptionsChainSettings>(defaultSettings.optionsChain)
        );
      }

      updateLocalStorage(SettingsEnum.OPTIONS_CHAIN);
    }

    if (sfxSettings) {
      const parsedSfxSettings = new Set(
        JSON.parse(sfxSettings) as SFXSettings[]
      );
      setSfx(parsedSfxSettings);
    } else {
      // Check if settings were all intentionally set as disabled
      if (optionsChain.has("disable_all")) {
        setSfx(new Set<SFXSettings>([]));
      } else {
        // If the key doesn't exist, re-populate settings
        setSfx(new Set<SFXSettings>(defaultSettings.sfx));
      }

      updateLocalStorage(SettingsEnum.SFX);
    }

    if (languageSetting) {
      setLanguage(languageSetting as Language);
      onChangeLanguage(languageSetting as Language);
    } else {
      updateLocalStorage(SettingsEnum.LANGUAGE);
    }

    // Direction (up/down) color scheme: read custom colors then the scheme, and
    // apply immediately so the whole app is themed on first paint. If no scheme
    // is stored yet, apply the classic default now — a geoblock-aware caller may
    // later seed a region default via seedDirectionSchemeFromCountry.
    const storedCustom = localStorage.getItem(SettingsEnum.CUSTOM_DIRECTION);
    const parsedCustom: DirectionColors = storedCustom
      ? (JSON.parse(storedCustom) as DirectionColors)
      : defaultSettings.customDirection;
    setCustomDirectionState(parsedCustom);

    const storedScheme = localStorage.getItem(
      SettingsEnum.DIRECTION_COLORS
    ) as DirectionSchemeName | null;
    if (storedScheme) {
      setDirectionScheme(storedScheme);
      applyDirectionColors(storedScheme, parsedCustom);
    } else {
      applyDirectionColors(DEFAULT_DIRECTION_SCHEME, parsedCustom);
    }

    // Market slippage: a corrupt or absent value must not leave an order
    // unbounded, so anything unusable clamps to the default.
    setSlippageBpsState(
      clampSlippageBps(localStorage.getItem(SettingsEnum.MARKET_SLIPPAGE))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSlippageBps = useCallback((bps: number) => {
    const safe = clampSlippageBps(bps);
    setSlippageBpsState(safe);
    localStorage.setItem(SettingsEnum.MARKET_SLIPPAGE, String(safe));
  }, []);

  const addOptionsChain = useCallback(
    (code: OptionsChainSettings) => {
      optionsChain.add(code);
      // Remove disable_all if there are other settings added
      if (optionsChain.size > 1 && optionsChain.has("disable_all"))
        optionsChain.delete("disable_all");
      setOptionsChain(new Set(optionsChain));
      updateLocalStorage(SettingsEnum.OPTIONS_CHAIN);
    },
    [optionsChain, updateLocalStorage]
  );

  const removeOptionsChain = useCallback(
    (code: OptionsChainSettings) => {
      optionsChain.delete(code);
      // Add disable_all if all settings are intentionally disabled
      if (optionsChain.size === 0) optionsChain.add("disable_all");
      setOptionsChain(new Set(optionsChain));
      updateLocalStorage(SettingsEnum.OPTIONS_CHAIN);
    },
    [optionsChain, updateLocalStorage]
  );

  const addSfx = useCallback(
    (code: SFXSettings) => {
      sfx.add(code);
      // Remove disable_all if there are other settings added
      if (sfx.size > 1 && sfx.has("disable_all")) sfx.delete("disable_all");
      setSfx(new Set(sfx));
      updateLocalStorage(SettingsEnum.SFX);
    },
    [sfx, updateLocalStorage]
  );

  const removeSfx = useCallback(
    (code: SFXSettings) => {
      sfx.delete(code);
      // Add disable_all if all settings are intentionally disabled
      if (sfx.size === 0) sfx.add("disable_all");
      setSfx(new Set(sfx));
      updateLocalStorage(SettingsEnum.SFX);
    },
    [sfx, updateLocalStorage]
  );

  const changeLanguage = useCallback((l: Language) => {
    setLanguage(l);
    localStorage.setItem(SettingsEnum.LANGUAGE, l);
    onChangeLanguage(l);
  }, []);

  const changeDirectionScheme = useCallback(
    (scheme: DirectionSchemeName) => {
      setDirectionScheme(scheme);
      localStorage.setItem(SettingsEnum.DIRECTION_COLORS, scheme);
      applyDirectionColors(scheme, customDirection);
    },
    [customDirection]
  );

  const setCustomDirection = useCallback((colors: DirectionColors) => {
    setCustomDirectionState(colors);
    setDirectionScheme("custom");
    localStorage.setItem(SettingsEnum.CUSTOM_DIRECTION, JSON.stringify(colors));
    localStorage.setItem(SettingsEnum.DIRECTION_COLORS, "custom");
    applyDirectionColors("custom", colors);
  }, []);

  const seedDirectionSchemeFromCountry = useCallback(
    (country?: string) => {
      // Only seed if the user has never chosen/seeded a scheme.
      if (localStorage.getItem(SettingsEnum.DIRECTION_COLORS)) return;
      const scheme = countryToScheme(country);
      setDirectionScheme(scheme);
      localStorage.setItem(SettingsEnum.DIRECTION_COLORS, scheme);
      applyDirectionColors(scheme, customDirection);
    },
    [customDirection]
  );

  return (
    <SettingsContext.Provider
      value={{
        sfx,
        addSfx,
        removeSfx,
        optionsChain,
        addOptionsChain,
        removeOptionsChain,
        language,
        changeLanguage,
        directionScheme,
        customDirection,
        changeDirectionScheme,
        setCustomDirection,
        seedDirectionSchemeFromCountry,
        slippageBps,
        setSlippageBps,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}
